import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { API_BASE_URL } from '../constants/apiConstants';
import { storeTokens, storeUserId } from '../utils/auth';

const AuthCallback = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('processing');
  const [error, setError] = useState(null);
  const hasExchanged = useRef(false);

  useEffect(() => {
    const handleCallback = async () => {
      // Prevent duplicate exchanges (React StrictMode runs effects twice)
      if (hasExchanged.current) {
        console.log('⚠️ Token exchange already in progress, skipping...');
        return;
      }
      hasExchanged.current = true;

      try {
        // Get authorization code from URL
        const code = searchParams.get('code');
        const errorParam = searchParams.get('error');
        const errorDescription = searchParams.get('error_description');

        // Check for errors from Cognito
        if (errorParam) {
          throw new Error(errorDescription || errorParam);
        }

        if (!code) {
          throw new Error('No authorization code received from Cognito');
        }

        console.log('✅ Authorization code received, exchanging for tokens...');
        setStatus('exchanging');

        // Retrieve PKCE code verifier from sessionStorage
        const codeVerifier = sessionStorage.getItem('pkce_code_verifier');
        if (!codeVerifier) {
          throw new Error('PKCE code verifier not found. Please try signing in again.');
        }

        console.log('🔐 PKCE code verifier retrieved from storage');

        // Exchange code for tokens via backend (include code_verifier)
        const response = await fetch(`${API_BASE_URL}/auth/callback?code=${code}&code_verifier=${codeVerifier}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json'
          }
        });

        // Clear code verifier from storage
        sessionStorage.removeItem('pkce_code_verifier');

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.error || 'Failed to exchange authorization code');
        }

        console.log('✅ Tokens received from Cognito');

        // Store tokens
        storeTokens({
          accessToken: data.access_token,
          idToken: data.id_token,
          refreshToken: data.refresh_token
        });

        setStatus('success');

        // Post-login verification
        console.log('🔐 Verifying user and setting up account...');
        
        const postLoginResponse = await fetch(`${API_BASE_URL}/auth/post-login`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${data.id_token}`
          }
        });

        const postLoginData = await postLoginResponse.json();

        if (postLoginResponse.ok) {
          console.log('✅ User verified:', postLoginData);

          // Store userId so all features can identify the logged-in user
          if (postLoginData.user?.userId) {
            storeUserId(postLoginData.user.userId);
            console.log('✅ UserId stored:', postLoginData.user.userId);
          }

          // Get onboarding status from backend
          const onboardingComplete = postLoginData.onboardingComplete || false;
          
          setTimeout(() => {
            navigate(onboardingComplete ? '/adflyn-chatbot' : '/webpage-analyzer');
          }, 1000);
        } else {
          console.warn('⚠️ Post-login verification failed, redirecting to web analyzer...');
          
          setTimeout(() => {
            navigate('/webpage-analyzer');
          }, 1000);
        }

      } catch (error) {
        console.error('❌ Authentication callback error:', error);
        setStatus('error');
        setError(error.message);
        
        // Redirect to login after 3 seconds
        setTimeout(() => {
          navigate('/?error=' + encodeURIComponent(error.message));
        }, 3000);
      }
    };

    handleCallback();
  }, [searchParams, navigate]);

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '100vh',
      padding: '20px',
      backgroundColor: '#f7f9fc'
    }}>
      <div style={{
        maxWidth: '500px',
        width: '100%',
        padding: '40px',
        backgroundColor: 'white',
        borderRadius: '12px',
        boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
        textAlign: 'center'
      }}>
        {status === 'processing' && (
          <>
            <div style={{
              width: '60px',
              height: '60px',
              border: '4px solid #f3f3f3',
              borderTop: '4px solid #6366f1',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              margin: '0 auto 20px'
            }}></div>
            <h2 style={{ color: '#1f2937', marginBottom: '10px' }}>
              Processing Authorization...
            </h2>
            <p style={{ color: '#6b7280' }}>
              Please wait while we verify your account
            </p>
          </>
        )}

        {status === 'exchanging' && (
          <>
            <div style={{
              width: '60px',
              height: '60px',
              border: '4px solid #f3f3f3',
              borderTop: '4px solid #6366f1',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              margin: '0 auto 20px'
            }}></div>
            <h2 style={{ color: '#1f2937', marginBottom: '10px' }}>
              Exchanging Tokens...
            </h2>
            <p style={{ color: '#6b7280' }}>
              Securely connecting to AWS Cognito
            </p>
          </>
        )}

        {status === 'success' && (
          <>
            <div style={{
              width: '60px',
              height: '60px',
              backgroundColor: '#10b981',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 20px',
              color: 'white',
              fontSize: '30px'
            }}>
              ✓
            </div>
            <h2 style={{ color: '#1f2937', marginBottom: '10px' }}>
              Success!
            </h2>
            <p style={{ color: '#6b7280' }}>
              Redirecting to your dashboard...
            </p>
          </>
        )}

        {status === 'error' && (
          <>
            <div style={{
              width: '60px',
              height: '60px',
              backgroundColor: '#ef4444',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 20px',
              color: 'white',
              fontSize: '30px'
            }}>
              ✕
            </div>
            <h2 style={{ color: '#1f2937', marginBottom: '10px' }}>
              Authentication Failed
            </h2>
            <p style={{ color: '#6b7280', marginBottom: '20px' }}>
              {error || 'An error occurred during authentication'}
            </p>
            <p style={{ color: '#9ca3af', fontSize: '14px' }}>
              Redirecting to login page...
            </p>
          </>
        )}
      </div>

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default AuthCallback;
